import { useQuery } from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import PostBlocks from '../../../components/Post/PostBlocks/PostBlocks';
import { useAuth } from '../../../context/AuthContext';
import { GET_POST_VERSIONS_QUERY } from '../../../graphql/resolvers/postQueries/getPostVersions.gql';
import type { TextBlock } from '../../../interfaces/textEditor.interface';
import styles from './post-history.module.scss';

// TRIL-275: author-only, full-width page that renders a post's original →
// rewrite evolution. textBlocksVersions[].blocks is hydrated server-side
// (TRIL-220); each entry has { version, contentRef, timestamp, id, blocks }.
const PostHistory = () => {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data, loading } = useQuery(GET_POST_VERSIONS_QUERY, {
    variables: { postId: postId ?? '' },
    skip: !postId,
    fetchPolicy: 'cache-and-network',
  });
  const post = data?.getPostById;

  // Newest version first (the "current" content at top), original at the bottom.
  const versions = useMemo(() => {
    const list = (post?.textBlocksVersions ?? []).filter(
      (v): v is NonNullable<typeof v> => v != null,
    );
    return [...list].sort((a, b) => (b.version ?? 0) - (a.version ?? 0));
  }, [post?.textBlocksVersions]);

  const [selectedId, setSelectedId] = useState<string | null>(null);

  const authorId =
    post?.authorId ?? post?.postAnalytics?.author?.userId ?? null;
  const isAuthor = !!authorId && authorId === user?.id;

  // Author-only: send everyone else back to the post.
  useEffect(() => {
    if (post && !isAuthor) {
      navigate(`/post/${postId}`, { replace: true });
    }
  }, [post, isAuthor, navigate, postId]);

  // Default to the latest version once data arrives.
  useEffect(() => {
    if (versions.length > 0 && selectedId === null) {
      setSelectedId(versions[0].id ?? null);
    }
  }, [versions, selectedId]);

  if (loading && !post) {
    return <div className={styles.page}>Loading…</div>;
  }
  if (!post || !isAuthor) {
    return null; // redirect handled in the effect
  }

  if (versions.length < 2) {
    return (
      <div className={styles.page}>
        <header className={styles.header}>
          <h1 className={styles.title}>Version history</h1>
          <Link to={`/post/${postId}`} className={styles.back}>
            ← Back to post
          </Link>
        </header>
        <p className={styles.empty}>
          This post hasn't been revised yet — there's only one version.
        </p>
      </div>
    );
  }

  const selected = versions.find((v) => v.id === selectedId) ?? versions[0];
  const latestVersion = versions[0].version ?? versions.length;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Version history</h1>
        <Link to={`/post/${postId}`} className={styles.back}>
          ← Back to post
        </Link>
      </header>

      <div className={styles.layout}>
        <aside className={styles.timeline} aria-label="Versions">
          {versions.map((v) => {
            const isLatest = v.version === latestVersion;
            const isSelected = v.id === selected.id;
            return (
              <button
                type="button"
                key={v.id ?? v.contentRef ?? v.version}
                className={`${styles.versionItem}${
                  isSelected ? ` ${styles.isSelected}` : ''
                }`}
                aria-current={isSelected}
                onClick={() => setSelectedId(v.id ?? null)}
              >
                <span className={styles.versionLabel}>
                  v{v.version}
                  {isLatest && <span className={styles.badge}>Current</span>}
                  {v.version === 1 && (
                    <span className={styles.badge}>Original</span>
                  )}
                </span>
                {v.timestamp && (
                  <span className={styles.versionDate}>
                    {new Date(v.timestamp).toLocaleString()}
                  </span>
                )}
              </button>
            );
          })}
        </aside>

        <main className={styles.viewer}>
          <PostBlocks blocks={(selected.blocks ?? []) as TextBlock[]} />
        </main>
      </div>
    </div>
  );
};

export default PostHistory;
